import { FlexComponentMap } from "components/flexFramework/FlexComponents/typings";
import { Logger } from "bernie-logger";
import * as React from "react";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { ServerSideLogger } from "components/utility/ServerSideLogger";
import { UNSUPPORTED_MODULE } from "config/systemEvents";
import { Component, ComponentProps } from "src/typings/flexFramework/FlexComponent";
import { Localization } from "bernie-l10n";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";

/**
 * Base class to describe a component that we are rendering in Blossom.
 * This is an adapter pattern over the components given to use from whatever data source (e.g. LPT Composition).
 *
 * This class knows what it is, and knows how to render itself appropriately regardless of where its data comes from.
 * The BlossomRenderer class will take a list of Blossom Components to render an entire page.
 */
export abstract class BlossomComponent {
  public fmId: number;
  public fmTitleId?: number;
  public name: string;
  public type: string;
  public modules: Component[];
  public componentMap: FlexComponentMap;
  public abstract getProps: () => any;
  public abstract getId: () => string | undefined;
  public abstract shouldRender: () => boolean;
  public abstract getView: () => string;
  public abstract getName: () => string;
  public abstract getBlossomComponents: () => BlossomComponent[];
  public abstract getModel: <T>() => T;

  protected constructor(module: Component, componentMap: FlexComponentMap) {
    this.name = module.name;
    this.fmId = module.fmId;
    this.fmTitleId = module.fmTitleId;
    this.modules = module.modules;
    this.componentMap = componentMap;
  }

  public render = (
    key: string,
    context: ExtendedContextStore,
    l10n: Localization,
    logger: Logger
  ) => {
    if (!this.shouldRender()) {
      return null;
    }

    const component = this.getReactComponent();
    if (component === undefined) {
      this.logUnsupportedModule(logger, context);

      return null;
    }

    const props: ComponentProps = {
      ...this.getProps(),
      key,
      context,
      l10n
    };

    return React.createElement(component.codeSplit, props);
  };

  public renderChildren = (
    logger: Logger,
    l10n: Localization,
    context: ExtendedContextStore,
    filterFunction?: (component: BlossomComponent) => boolean
  ) => {
    const keyHelper = new ItemKeyHelper("blossomComponent");
    let children = this.getBlossomComponents();
    if (filterFunction) {
      children = children.filter(filterFunction);
    }

    return children
      .map(component =>
        component.render(component.getId() || keyHelper.next(), context, l10n, logger)
      )
      .filter(component => component) as React.ReactElement[];
  };

  /**
   * Gets the react component from the Flex Component Map using its name and view.
   * The convention is: <module name>_<view>
   *
   * e.g. "hotels_travellers-loved"
   */
  public getReactComponent = () => {
    const flexComponentName = this.getName();
    const view = this.getView();

    // If there is a view, let's try to get it from the map
    if (view && this.componentMap.has(`${flexComponentName}_${view}`)) {
      return this.componentMap.get(`${flexComponentName}_${view}`);
    }

    // Default
    return this.componentMap.get(flexComponentName);
  };

  /**
   * Takes the name of a Flex component found in a composition that had no corresponding Flex UI Component, and reports it to the server side logger
   */
  private logUnsupportedModule = (logger: Logger, context: ExtendedContextStore) => {
    const contextualInfo = Object.entries({
      pageType: context && context.searchContext && context.searchContext.pageType,
      deviceId: context && context.deviceId,
      userAgent: context && context.userAgent,
      langId: context && context.langId,
      locale: context && context.locale,
      siteId: context && context.site && context.site.id,
      requestId: context && context.traceInfo && context.traceInfo["Trace-ID"]
    })
      .map((key, value) => `${key}=\"${value}\"`)
      .toString();

    ServerSideLogger(logger!).logEvent(
      UNSUPPORTED_MODULE,
      `moduleName=${this.name} is not currently supported by this view layer application`,
      contextualInfo
    );
  };
}
